/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IActionContext } from '@msdyn365-commerce/core';
import { addCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { Cart, CartLine } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import { ICartActionResultWithCart } from './cart-action-result';

/**
 * Get the quantity limit.
 * @param maxQuantityForCartLineItem - Max quantity for cart line.
 * @param inputItem - The input item.
 * @param inputItem.cartLineToAdd - Cart line to add.
 * @param inputItem.availableQuantity - Available quantity.
 * @param inputItem.shouldEnableStockCheck - Is stock check enabled.
 * @param inputItem.isAddServiceItemToCart - Is add service item to cart.
 * @returns The quantity limit.
 */
function getQuantityLimit(
    maxQuantityForCartLineItem: number | undefined,
    inputItem: {
        cartLineToAdd: CartLine;
        availableQuantity?: number;
        shouldEnableStockCheck?: boolean;
        isAddServiceItemToCart?: boolean;
    }
): number {
    let quantityLimit: number;
    const defaultSiteMaxQuantity = 10;

    if (inputItem.shouldEnableStockCheck === false) {
        quantityLimit = maxQuantityForCartLineItem ?? defaultSiteMaxQuantity;
    } else if (maxQuantityForCartLineItem === undefined && inputItem.availableQuantity === undefined) {
        quantityLimit = defaultSiteMaxQuantity;
    } else if (inputItem.availableQuantity === undefined) {
        quantityLimit = maxQuantityForCartLineItem ?? defaultSiteMaxQuantity;
    } else if (maxQuantityForCartLineItem === undefined) {
        quantityLimit = inputItem.availableQuantity;
    } else {
        quantityLimit = Math.min(maxQuantityForCartLineItem, inputItem.availableQuantity);
    }

    return quantityLimit;
}

/**
 * Get the index of the item to add.
 * @param lines - The cart lines.
 * @param cartLineToAdd - The cart line to add.
 * @returns The index of the item to add.
 */
function getElementIndex(lines: CartLine[], cartLineToAdd: CartLine): number {
    let elementFoundAt: number = -1;
    const productIdToFind = cartLineToAdd.ProductId;

    for (const [index, line] of lines.entries()) {
        if (
            line.ProductId === productIdToFind &&
            (line.DeliveryMode || '') === (cartLineToAdd.DeliveryMode || '') &&
            (line.FulfillmentStoreId || '') === (cartLineToAdd.FulfillmentStoreId || '')
        ) {
            elementFoundAt = index;

            break;
        }
    }

    return elementFoundAt;
}

async function addProductsToCartValidateMaxAgainstDefaultOrderSettings(
    cart: Readonly<Cart>,
    actionContext: IActionContext,
    input: {
        cartLineToAdd: CartLine;
        availableQuantity?: number;
        enableStockCheck?: boolean;
        isAddServiceItemToCart?: boolean;
    }[]
): Promise<ICartActionResultWithCart> {
    if (!cart.CartLines) {
        return { cart: undefined, status: 'FAILED', substatus: 'EMPTYCART' };
    }

    for (const inputItem of input) {
        if (inputItem.enableStockCheck && inputItem.availableQuantity) {
            let elementFoundAt: number = -1;
            const productIdToFind = inputItem.cartLineToAdd.ProductId;

            for (let i = 0; i < cart.CartLines.length; i++) {
                if (
                    cart.CartLines[i].ProductId === productIdToFind &&
                    (cart.CartLines[i].DeliveryMode || '') === (inputItem.cartLineToAdd.DeliveryMode || '') &&
                    (cart.CartLines[i].FulfillmentStoreId || '') === (inputItem.cartLineToAdd.FulfillmentStoreId || '')
                ) {
                    elementFoundAt = i;

                    break;
                }
            }

            if (!inputItem.isAddServiceItemToCart && elementFoundAt !== -1) {
                const cartLineToUpdate = { ...cart.CartLines[elementFoundAt] };
                const curQuantity = cartLineToUpdate.Quantity || 0;

                if (curQuantity + (inputItem.cartLineToAdd.Quantity || 1) > inputItem.availableQuantity) {
                    return {
                        cart: {
                            Id: cart.Id,
                            CartLines: cart.CartLines
                        },
                        status: 'FAILED',
                        substatus: 'MAXQUANTITY'
                    };
                }
            }
        }
    }

    const newCartLines = input.map(inputItem => {
        const cartLine = { ...inputItem.cartLineToAdd };
        return cartLine;
    });
    let addCartLinesResult: ICartActionResultWithCart;

    if (cart.Version) {
        return addCartLinesAsync({ callerContext: actionContext }, cart.Id, newCartLines, cart.Version)
            .then(newCart => {
                addCartLinesResult = { cart: newCart, status: 'SUCCESS', substatus: 'PRODUCTADDED' };
                return addCartLinesResult;
            })
            .catch(error => {
                actionContext.telemetry.trace(error);
                actionContext.telemetry.trace('Unable to add Cart Line');

                if (
                    error.name === 'Microsoft_Dynamics_Commerce_Runtime_AddingItem_ExceedsMaximumQuantity' ||
                    error.name === 'Microsoft_Dynamics_Commerce_Runtime_UpdatingItem_ExceedsMaximumQuantity'
                ) {
                    addCartLinesResult = {
                        cart: undefined,
                        status: 'FAILED',
                        substatus: 'QUANTITYLIMITS',
                        errorDetails: { LocalizedMessage: error.message }
                    };
                    return addCartLinesResult;
                }

                const hasInvoiceInCart = cart.CartLines?.find(cartline => cartline.IsInvoiceLine === true);
                if (hasInvoiceInCart) {
                    addCartLinesResult = {
                        cart: undefined,
                        status: 'FAILED',
                        substatus: 'INVOICEINCART',
                        errorDetails: { LocalizedMessage: error.message }
                    };
                    return addCartLinesResult;
                }

                addCartLinesResult = {
                    cart: undefined,
                    status: 'FAILED',
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- existing error type.
                    errorDetails: { LocalizedMessage: error.message },
                    validationResults: error.data?.ValidationResults
                };

                return addCartLinesResult;
            });
    }
    actionContext.telemetry.warning('Unable to update Cart Line, Cart Version could not be found');

    addCartLinesResult = { cart: undefined, status: 'FAILED', substatus: 'NOCARTVERSION' };
    return addCartLinesResult;
}

async function addProductsToCartValidateMaxAgainstSiteSettings(
    cart: Readonly<Cart>,
    actionContext: IActionContext,
    input: {
        cartLineToAdd: CartLine;
        availableQuantity?: number;
        enableStockCheck?: boolean;
        isAddServiceItemToCart?: boolean;
    }[]
): Promise<ICartActionResultWithCart> {
    if (!cart.CartLines) {
        return { cart: undefined, status: 'FAILED', substatus: 'EMPTYCART' };
    }

    const maxQuantityForCartLineItem: number | undefined = actionContext.requestContext.app.config.maxQuantityForCartLineItem;

    for (const inputItem of input) {
        const quantityLimit: number = getQuantityLimit(maxQuantityForCartLineItem, {
            cartLineToAdd: inputItem.cartLineToAdd,
            availableQuantity: inputItem.availableQuantity,
            shouldEnableStockCheck: inputItem.enableStockCheck,
            isAddServiceItemToCart: inputItem.isAddServiceItemToCart
        });
        const elementFoundAt: number = getElementIndex(cart.CartLines, inputItem.cartLineToAdd);

        if (!inputItem.isAddServiceItemToCart && elementFoundAt !== -1) {
            const cartLineToUpdate = { ...cart.CartLines[elementFoundAt] };
            const currentQuantity = cartLineToUpdate.Quantity || 0;

            if (currentQuantity + (inputItem.cartLineToAdd.Quantity || 1) > quantityLimit) {
                return {
                    cart: {
                        Id: cart.Id,
                        CartLines: cart.CartLines
                    },
                    status: 'FAILED',
                    substatus: 'MAXQUANTITY'
                };
            }
        }

        if (
            !inputItem.isAddServiceItemToCart &&
            inputItem.cartLineToAdd &&
            inputItem.cartLineToAdd.Quantity &&
            inputItem.cartLineToAdd.Quantity > quantityLimit
        ) {
            return {
                cart: {
                    Id: cart.Id,
                    CartLines: cart.CartLines
                },
                status: 'FAILED',
                substatus: 'MAXQUANTITY'
            };
        }
    }

    const newCartLines = input.map(inputItem => {
        const cartLine = { ...inputItem.cartLineToAdd };
        cartLine.Quantity = cartLine.Quantity || 1;
        return cartLine;
    });
    let cartActionResult: ICartActionResultWithCart;

    if (cart.Version) {
        return addCartLinesAsync({ callerContext: actionContext }, cart.Id, newCartLines, cart.Version)
            .then(newCart => {
                cartActionResult = { cart: newCart, status: 'SUCCESS', substatus: 'PRODUCTADDED' };
                return cartActionResult;
            })
            .catch(error => {
                actionContext.telemetry.trace(error);
                actionContext.telemetry.trace('Unable to add Cart Line');

                const hasInvoiceInCart = cart.CartLines?.find(cartline => cartline.IsInvoiceLine === true);
                if (hasInvoiceInCart) {
                    cartActionResult = {
                        cart: undefined,
                        status: 'FAILED',
                        substatus: 'INVOICEINCART',
                        errorDetails: { LocalizedMessage: error.message }
                    };
                    return cartActionResult;
                }

                cartActionResult = {
                    cart: undefined,
                    status: 'FAILED',
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- existing error type.
                    errorDetails: { LocalizedMessage: error.message },
                    validationResults: error.data?.ValidationResults
                };
                return cartActionResult;
            });
    }
    actionContext.telemetry.warning('Unable to update Cart Line, Cart Version could not be found');

    cartActionResult = { cart: undefined, status: 'FAILED', substatus: 'NOCARTVERSION' };
    return cartActionResult;
}

/**
 * Add products to cart without site settings validation.
 * @param cart - The cart.
 * @param actionContext - The action context.
 * @param input - The cart lines to add.
 * @returns The results.
 */
async function addProductsToCartWithoutSiteSettings(
    cart: Readonly<Cart>,
    actionContext: IActionContext,
    input: {
        cartLineToAdd: CartLine;
    }[]
): Promise<ICartActionResultWithCart> {
    if (!cart.CartLines) {
        return { cart: undefined, status: 'FAILED', substatus: 'EMPTYCART' };
    }

    const defaultQuantity = 1;
    const newCartLines = input.map(inputItem => {
        const cartLine = { ...inputItem.cartLineToAdd };
        cartLine.Quantity = cartLine.Quantity ?? defaultQuantity;
        return cartLine;
    });
    let addCartLinesResult: ICartActionResultWithCart;

    if (cart.Version) {
        return addCartLinesAsync({ callerContext: actionContext }, cart.Id, newCartLines, cart.Version)
            .then(newCart => {
                addCartLinesResult = { cart: newCart, status: 'SUCCESS', substatus: 'PRODUCTADDED' };
                return addCartLinesResult;
            })
            .catch(error => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- existing error type.
                actionContext.telemetry.trace(error);
                actionContext.telemetry.trace('Unable to add cart lines');

                addCartLinesResult = {
                    cart: undefined,
                    status: 'FAILED',
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- existing error type.
                    errorDetails: { LocalizedMessage: error.message },
                    validationResults: error.data?.ValidationResults
                };
                return addCartLinesResult;
            });
    }

    actionContext.telemetry.warning('Unable to update cart lines, cart version could not be found');

    addCartLinesResult = { cart: undefined, status: 'FAILED', substatus: 'NOCARTVERSION' };
    return addCartLinesResult;
}

export async function addProductsToCartInternal(
    cart: Readonly<Cart>,
    actionContext: IActionContext,
    input: {
        cartLineToAdd: CartLine;
        availableQuantity?: number;
        isStockCheckEnabled?: boolean;
        isAddServiceItemToCart?: boolean;
    }[]
): Promise<ICartActionResultWithCart> {
    return addProductsToCartWithoutSiteSettings(cart, actionContext, input);
}

export async function addProductToCartInternal(
    cart: Readonly<Cart>,
    cartLineToAdd: CartLine,
    actionContext: IActionContext,
    orderQuantityLimitsFeatureIsEnabled: boolean,
    availableQuantity?: number,
    enableStockCheck?: boolean,
    isAddServiceItemToCart?: boolean,
    isMaxByQuantityValidationFeatureDefined?: boolean,
    shouldSkipSiteSettings?: boolean
): Promise<ICartActionResultWithCart> {
    if (shouldSkipSiteSettings) {
        return addProductsToCartWithoutSiteSettings(cart, actionContext, [{ cartLineToAdd }]);
    }

    // Using max from the default order settings if it's defined there and quantity limits feature is enabled.
    // Otherwise use max from site settings or default max 10.
    if (isMaxByQuantityValidationFeatureDefined && orderQuantityLimitsFeatureIsEnabled) {
        return addProductsToCartValidateMaxAgainstDefaultOrderSettings(cart, actionContext, [
            { cartLineToAdd, availableQuantity, enableStockCheck, isAddServiceItemToCart }
        ]);
    }
    return addProductsToCartValidateMaxAgainstSiteSettings(cart, actionContext, [
        { cartLineToAdd, availableQuantity, enableStockCheck, isAddServiceItemToCart }
    ]);
}

export default addProductToCartInternal;
